import React, { useEffect, useState } from 'react';

interface SeasonalEffectsProps {
  currentSeason: string;
}

const SeasonalEffects: React.FC<SeasonalEffectsProps> = ({ currentSeason }) => {
  const [showEffects, setShowEffects] = useState(false);

  useEffect(() => {
    // Add a short delay before showing effects for better performance
    const timer = setTimeout(() => {
      setShowEffects(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Generate a random number between min and max
  const random = (min: number, max: number) => {
    return Math.random() * (max - min) + min;
  };

  // Create an array of elements with their properties
  const createElements = (count: number, className: string, styles: any = {}, isForeground: boolean = false) => {
    return Array.from({ length: count }).map((_, index) => {
      const elementStyles = {
        ...styles,
        animationDuration: `${random(5, 15)}s`,
        animationDelay: `${random(0, 5)}s`,
        left: `${random(0, 100)}%`,
      };
      
      return (
        <div
          key={`${className}-${index}`}
          className={`${className} ${isForeground ? 'foreground-decoration' : 'seasonal-decoration'} ${index % 10 === 0 ? 'featured-decoration' : ''}`}
          style={elementStyles}
        />
      );
    });
  };

  // Spring elements
  const renderSpring = () => {
    const blossoms = createElements(15, 'spring-blossom', {
      backgroundColor: '#FFCDD2',
      width: (_: number) => `${random(8, 15)}px`,
      height: (_: number) => `${random(8, 15)}px`,
      animationDuration: `${random(15, 30)}s`,
    });

    const raindrops = createElements(40, 'rain-drop', {
      animationDuration: `${random(1, 3)}s`,
      left: `${random(0, 100)}%`,
      opacity: 1,
    });

    // 10% of raindrops in the foreground
    const foregroundRaindrops = createElements(5, 'rain-drop', {
      animationDuration: `${random(1, 3)}s`,
      left: `${random(0, 100)}%`,
      opacity: 1,
    }, true);

    return (
      <>
        <div className="spring-sun"></div>
        {blossoms}
        {raindrops}
        {foregroundRaindrops}
      </>
    );
  };

  // Summer elements
  const renderSummer = () => {
    const clouds = createElements(5, 'summer-cloud', {
      top: `${random(5, 30)}%`,
      animationDuration: `${random(60, 90)}s`,
    });

    const bees = createElements(6, 'summer-bee', {
      top: `${random(30, 70)}%`,
      animationDuration: `${random(10, 20)}s`,
    });

    return (
      <>
        <div className="summer-sun"></div>
        {clouds}
        {bees}
      </>
    );
  };

  // Fall elements
  const renderFall = () => {
    const leafTypes = ['maple', 'oak', 'aspen'];
    const leafColors = ['#E65100', '#F57C00', '#FF9800', '#FFB74D'];
    
    const leaves = Array.from({ length: 40 }).map((_, index) => {
      const type = leafTypes[Math.floor(random(0, leafTypes.length))];
      const color = leafColors[Math.floor(random(0, leafColors.length))];
      const size = random(15, 30);
      const isForeground = index % 10 === 0; // 10% in foreground
      
      return (
        <div
          key={`leaf-${index}`}
          className={`leaf ${type} ${isForeground ? 'foreground-decoration' : 'seasonal-decoration'} ${index % 20 === 0 ? 'featured-decoration' : ''}`}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            backgroundColor: color,
            left: `${random(0, 100)}%`,
            animationDuration: `${random(10, 20)}s`,
            animationDelay: `${random(0, 10)}s`,
            transform: `rotate(${random(0, 360)}deg)`,
          }}
        />
      );
    });

    return (
      <>
        {leaves}
      </>
    );
  };

  // Winter elements
  const renderWinter = () => {
    // Create snowflakes in the background (90%)
    const snowflakes = Array.from({ length: 50 }).map((_, index) => {
      const size = random(5, 10);
      
      return (
        <div
          key={`snowflake-${index}`}
          className="snowflake seasonal-decoration"
          style={{
            width: `${size}px`,
            height: `${size}px`,
            left: `${random(0, 100)}%`,
            animationDuration: `${random(10, 15)}s`,
            animationDelay: `${random(0, 5)}s`,
          }}
        />
      );
    });

    // Create some snowflakes in the foreground (10%)
    const foregroundSnowflakes = Array.from({ length: 8 }).map((_, index) => {
      const size = random(8, 15);
      
      return (
        <div
          key={`snowflake-fg-${index}`}
          className="snowflake foreground-decoration featured-decoration"
          style={{
            width: `${size}px`,
            height: `${size}px`,
            left: `${random(0, 100)}%`,
            animationDuration: `${random(8, 12)}s`,
            animationDelay: `${random(0, 5)}s`,
          }}
        />
      );
    });

    // Add icicles at the top
    const icicles = Array.from({ length: 20 }).map((_, index) => {
      const width = random(2, 5);
      const height = random(20, 80);
      
      return (
        <div
          key={`icicle-${index}`}
          className="winter-icicle"
          style={{
            width: `${width}px`,
            height: `${height}px`,
            left: `${random(0, 100)}%`,
            opacity: 1,
          }}
        />
      );
    });

    return (
      <>
        <div className="winter-frost"></div>
        {snowflakes}
        {foregroundSnowflakes}
        {icicles}
      </>
    );
  };

  // Render seasonal elements based on current season
  const renderSeasonalElements = () => {
    if (!showEffects) return null;

    switch (currentSeason) {
      case 'spring':
        return renderSpring();
      case 'summer':
        return renderSummer();
      case 'fall':
        return renderFall();
      case 'winter':
        return renderWinter();
      default:
        return null;
    }
  };

  return (
    <div className="seasonal-effects-container">
      {renderSeasonalElements()}
    </div>
  );
};

export default SeasonalEffects; 